<template>
    <div class="container-online-sign">
        <section class="sign-required">
            <span class="sign-required-header">
                Assinaturas Requeridas
            </span>

            <span class="sign-required-configuration-default">
                Selecione as assinaturas que serão requeridas em todos os contratos de compra
                e venda das unidades do empreendimento <span class="enterprise">{{ product?.name || '-' }}.</span>
            </span>
        </section>

        <section class="container-marker-paper-signers">
            <div class="marker-paper-signers">
                <section class="content-marker-paper-signers">
                    <div class="signers">
                        <span>Assinantes</span>
                    </div>

                    <div class="role-subscription">
                        <div class="role">
                            <span class="role-type">Papel</span>
                        </div>

                        <div class="subscription">
                            <span class="subscription-type">Tipo de Assinatura</span>
                        </div>
                    </div>
                </section>

                <section
                    class="draggable"
                >
                    <draggable
                        :list="signers"
                        class="list-group"
                        ghost-class="ghost"
                        :animation="200"
                        @start="dragging = true"
                        @end="dragging = false"
                        @change="setSigners(signers)"
                    >
                        <div
                            v-for="(signer, index) in signers"
                            :key="index"
                            class="list-group-item"
                            data-ui="signer-identify"
                        >
                            <div class="list-items">
                                <div class="draggables">
                                    <div class="drag-image" data-ui="button-move-signer-position">
                                        <img :src="require('@/assets/imgs/drag-and-drop.svg')" />
                                    </div>

                                    <div class="checkbox">
                                        <ui-checkbox
                                            v-model="signer.selected"
                                            design="mgb-0"
                                            :checked="signer.selected"
                                            :disabled="signer.type === 'customer'"
                                            :data-ui="'checkbox-signer'"
                                        />
                                    </div>

                                    <div class="infos-user">
                                        <div class="name">{{ signer.label }}</div>
                                    </div>
                                </div>

                                <div class="role-type-options">
                                    <div class="role">
                                        <ui-select-v2
                                            v-model="signer.role"
                                            :data="roleOptions"
                                            :error="!!formValidation.errors[signer.group]?.role"
                                            :observation="formValidation.errors[signer.group]?.role"
                                            :data-ui-selected="'select-paper-sign'"
                                            :data-ui-search="'search-paper-sign'"
                                            :data-ui-options="'list-option-paper-sign'"
                                            :data-ui-cancel-button="'button-cancel-select-paper-sign'"
                                            :data-ui-selected-remove="'button-remove-select-option-paper-sign'"
                                            design="mgb-0"
                                            return-value="value"
                                            background-color="#FFF"
                                            placeholder="Selecione o papel"
                                        />
                                    </div>

                                    <div class="type">
                                        <ui-select-v2
                                            v-model="signer.signature_type"
                                            :data="typeOptions"
                                            :error="!!formValidation.errors[signer.group]?.signature_type"
                                            :observation="formValidation.errors[signer.group]?.signature_type"
                                            :data-ui-selected="'select-type-signature'"
                                            :data-ui-search="'search-type-signature'"
                                            :data-ui-options="'list-type-signature'"
                                            :data-ui-cancel-button="'button-cancel-select-type-signature'"
                                            :data-ui-selected-remove="'button-remove-select-type-signature'"
                                            return-value="value"
                                            design="mgb-0"
                                            background-color="#FFF"
                                            placeholder="Selecione o tipo de assinatura"
                                        />
                                    </div>

                                    <div v-if="signer.type === 'nonuser'" class="menu-card">
                                        <ui-menu-card>
                                            <ui-button @click="edit(signer)">
                                                Configurações
                                            </ui-button>

                                            <ui-button @click="removeSigner(index)">
                                                Excluir
                                            </ui-button>
                                        </ui-menu-card>
                                    </div>
                                </div>
                            </div>

                            <div v-if="hasErrors(signer)" class="errors-list">
                                <ul>
                                    <li
                                        v-for="(error, index) in formValidation.errors[signer.group]"
                                        :key="index"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </draggable>
                </section>
            </div>

            <div class="add-new-signers">
                <span class="new-signers" data-ui="modal-add-newSigner" @click="addSigner">
                    + Adicionar novo assinante
                </span>
            </div>
        </section>

        <section class="footer-online-sign">
            <div class="save-editions">
                <ui-button
                    :disabled="!formValidation?.valid"
                    design="primary mw-150 md"
                    :data-ui="'button-save-signers'"
                    @click="saveConfigs">
                    Salvar
                </ui-button>
            </div>
        </section>

        <ui-add-signers-modal mode="assine_online" />

        <ui-integration-modal />
    </div>
</template>

<script>
    import { uiSelectV2, uiCheckbox } from '@/components/ui/form'
    import { uiMenuCard } from '@/components/ui'
    import { uiButton } from '@/components/ui/buttons'
    import { uiAddSignersModal } from '@/components/ui-facilita/products'
    import uiIntegrationModal from './components/integration-modal'
    import draggable from 'vuedraggable'

    import { mapActions, mapGetters } from 'vuex'

    import { types, onlineRoles as roles } from '@/assets/js/data'
    import { utilMixin } from '@/mixins'

    export default {
        name: 'ui-online-sign',

        components: {
            uiSelectV2,
            uiCheckbox,
            uiMenuCard,
            uiButton,
            uiAddSignersModal,
            uiIntegrationModal,
            draggable
        },

        mixins: [utilMixin],

        data() {
            return {
                createContract: false,
                typeOptions: [...types],
                roleOptions: [...roles],
                unsubscribe: null
            }
        },

        computed: {
            ...mapGetters('digitalSignatureStore', {
                signers: 'getSigners',
                product: 'getProduct',
                integration: 'getIntegration',
                formValidation: 'getFormValidation'
            })
        },

        methods: {
            ...mapActions('digitalSignatureStore', [
                'setSigners',
                'saveConfigs',
                'removeSigner'
            ]),
            /**
             * Abre o modal para o cadastro de assinantes externos
             */
			addSigner() {
				this.$root.$emit('modal-open-assine_online-add-signers')
			},
            /**
             * Abre o modal para edição de um assinante externo
             */
            edit(signer) {
                this.$root.$emit('modal-open-assine_online-add-signers', signer)
            },
            /**
             * Verifica se existem erros de formulário referentes a determinado assinante
             */
            hasErrors(signer) {
                return Object.keys(this.formValidation.errors[signer.group] || {}).length
            }
        }
    }
</script>

<style lang="stylus" scoped>
    @import './styles.styl'
</style>